<template>
  <PTransparentLoader :loading="loading">
    <CContainer v-if="movementRefund">
      <CRow>
        <CCol lg="12">
          <div class="d-flex align-items-center mb-3">
            <CButton color="dark"
                     variant="outline"
                     class="btn-circle btn-circle--sm d-inline-flex align-items-center justify-content-center mr-2"
                     size="sm"
                     @click="goBack">
              <CIcon name="cipArrowLeft"/>
            </CButton>
            <h1 class="main-header mb-0">
              <translate translate-context="accounting.movement.refund">Order</translate>
              {{ movementRefund.order_number || '-' }}
            </h1>
          </div>

          <!-- Order information -->
          <CCard class="mb-3">
            <CCardHeader class="border-0 px-4 pb-1 pt-4">
              <div class="d-flex align-items-center font-weight-medium">
                <CIcon name="cipShoppingBag01" size="custom-size" :width="18" class="mr-2"/>
                <translate translate-context="accounting.movement.refund">Order information</translate>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="4" v-if="hasScopePerm('accounting.movements.fields.event_name')">
                  <div class="info-cell">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Event</translate>
                    </div>
                    <div class="info-value">{{ movementRefund.pica_service_name || '-' }}</div>
                  </div>
                </CCol>
                <CCol md="4">
                  <div class="info-cell">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Order Item</translate>
                    </div>
                    <div class="info-value">
                      <router-link
                        v-if="movementRefund.order_item_id"
                        :to="getOrderItemRoute(movementRefund.order_item_id)"
                        class="reference-link"
                      >
                        #{{ movementRefund.order_item_id }}
                      </router-link>
                      <span v-else>-</span>
                    </div>
                  </div>
                </CCol>
                <CCol md="4" v-if="hasScopePerm('accounting.movements.fields.order_number')">
                  <div class="info-cell">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Order</translate>
                    </div>
                    <div class="info-value">{{ movementRefund.order_number || '-' }}</div>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Refund details -->
          <CCard class="mb-3">
            <CCardHeader class="border-0 px-4 pb-1 pt-4">
              <div class="d-flex align-items-center font-weight-medium">
                <CIcon name="cipDoc" size="custom-size" :width="18" class="mr-2"/>
                <translate translate-context="accounting.movement.refund">Refund details</translate>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="4">
                  <div class="info-cell">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Pertinence date</translate>
                    </div>
                    <div class="info-value">{{ movementRefund.pertinence_date|formatDateTime('L') }}</div>
                  </div>
                </CCol>
                <CCol md="4">
                  <div class="info-cell">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Type</translate>
                    </div>
                    <div class="info-value">
                      <CBadge v-if="movementRefund.refund_type" color="primary" class="badge-outline">
                        {{ movementRefund.refund_type.name }}
                      </CBadge>
                      <span v-else>-</span>
                    </div>
                  </div>
                </CCol>
                <CCol md="4" v-if="hasScopePerm('accounting.movements.fields.created_by_email')">
                  <div class="info-cell">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Created by</translate>
                    </div>
                    <div class="info-value">{{ movementRefund.created_by_email || '-' }}</div>
                  </div>
                </CCol>
                <CCol md="4">
                  <div class="info-cell">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Created at</translate>
                    </div>
                    <div class="info-value">{{ movementRefund.created_at|formatDateTime('L LT') }}</div>
                  </div>
                </CCol>
                <CCol md="12" v-if="movementRefund.reason">
                  <div class="info-cell mt-3">
                    <div class="info-label">
                      <translate translate-context="accounting.movement.refund">Reason</translate>
                    </div>
                    <PAlert color="warning">
                      {{ movementRefund.reason }}
                    </PAlert>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Revenue breakdown -->
          <CCard class="mb-3" v-if="hasRevenueBreakdown">
            <CCardHeader class="border-0 px-4 pb-1 pt-4">
              <div class="d-flex align-items-center font-weight-medium">
                <CIcon name="cipStats" size="custom-size" :width="18" class="mr-2"/>
                <translate translate-context="accounting.movement.refund">Revenue breakdown</translate>
              </div>
            </CCardHeader>
            <CCardBody>
              <!-- Waterfall: Gross → VAT → Net -->
              <div class="order-row" v-if="hasScopePerm('accounting.movements.fields.gross')">
                <div class="label" v-translate translate-context="accounting.movement.refund">Gross refund</div>
                <div class="value text-danger">-{{ movementRefund.gross|currency(refundCurrency) }}</div>
              </div>
              <div class="order-row" v-if="hasScopePerm('accounting.movements.fields.vat')">
                <div class="label" v-translate translate-context="accounting.movement.refund">VAT</div>
                <div class="value">{{ movementRefund.vat|currency(refundCurrency) }}</div>
              </div>
              <div class="order-row order-row--border font-weight-semi-bold" v-if="hasScopePerm('accounting.movements.fields.net_revenue')">
                <div class="label" v-translate translate-context="accounting.movement.refund">Net refund</div>
                <div class="value text-danger">-{{ movementRefund.net|currency(refundCurrency) }}</div>
              </div>
              <div class="order-row" v-if="hasTransactionCost">
                <div class="label" v-translate translate-context="accounting.movement.refund">Transaction cost</div>
                <div class="value">{{ movementRefund.transaction_cost|currency(refundCurrency) }}</div>
              </div>
              <div class="order-row order-row--border font-weight-semi-bold" v-if="hasTransactionCost && hasStakeholderSummary">
                <div class="label" v-translate translate-context="accounting.movement.refund">Total payout</div>
                <div class="value">{{ stakeholderTotal|currency(refundCurrency) }}</div>
              </div>

              <!-- Stakeholder split -->
              <CRow v-if="hasStakeholderSummary" class="mt-3">
                <CCol
                  v-for="(item, index) in stakeholderSummaryRows"
                  :key="index"
                  lg="6"
                >
                  <div class="payout-cell payout-cell--border">
                    <div class="label">{{ item.label }}</div>
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="value">{{ item.total|currency(refundCurrency) }}</div>
                      <!--<div class="ml-1">{{ item.percentage }}%</div>-->
                    </div>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  </PTransparentLoader>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import PAlert from '@/components/PAlert.vue'
import {
  getMovementRoutePrefix,
  getMovementDetailRouteParams,
  MOVEMENT_TYPES,
  STAKEHOLDER_TYPE_LABELS
} from '../constants'
import ConfigScope from "@/domain/core/constant/configScope"

export default {
  name: 'MovementRefundDetail',
  components: {
    PAlert,
    PTransparentLoader
  },
  data() {
    return {
      loading: true
    }
  },
  async mounted() {
    await this.setScope(this.scope)
    await this.fetchScopedConfig()
    await this.loadRefund()
  },
  computed: {
    ...mapGetters('accounting/movement', ['movementRefund']),

    scopeType() {
      return this.$route.meta.scopeType || 'pica-service'
    },

    scopeId() {
      if (this.scopeType === 'global') {
        return null
      } else if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      return getMovementRoutePrefix(this.scopeType)
    },

    refundCurrency() {
      return this.movementRefund?.currency || 'EUR'
    },

    hasStakeholderSummary() {
      return this.movementRefund?.stakeholder_summary?.length > 0
    },

    hasRevenueBreakdown() {
      return this.movementRefund && this.movementRefund.gross != null
    },

    stakeholderPermissionMap() {
      return {
        'PICA': 'accounting.movements.fields.net_pica',
        'PHOTO_SERVICE': 'accounting.movements.fields.net_photo_service',
      }
    },

    stakeholderSummaryRows() {
      if (!this.hasStakeholderSummary) return []
      const filtered = this.movementRefund.stakeholder_summary.filter(s => {
        const typeKey = (s.stakeholder_type || '').toUpperCase()
        const perm = this.stakeholderPermissionMap[typeKey]
        return !perm || this.hasScopePerm(perm)
      })
      const stakeholderTotal = filtered.reduce(
        (sum, s) => sum + (Math.abs(parseFloat(s.total)) || 0), 0
      )
      return filtered.map(s => {
        const typeKey = (s.stakeholder_type || '').toUpperCase()
        const total = parseFloat(s.total) || 0
        return {
          label: STAKEHOLDER_TYPE_LABELS[typeKey] || s.stakeholder_type,
          total: s.total,
          percentage: stakeholderTotal > 0 ? ((Math.abs(total) / stakeholderTotal) * 100).toFixed(1) : '0.0'
        }
      })
    },

    stakeholderTotal() {
      return this.stakeholderSummaryRows.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0)
    },

    hasTransactionCost() {
      return this.movementRefund && parseFloat(this.movementRefund.transaction_cost) > 0
    }
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/movement', ['fetchMovementRefund']),
    ...mapActions('login', ['fetchConfig']),

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        } else {
          await this.fetchConfig({
            type: 'accounting_global'
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet
      }
    },

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({ name: this.listRouteName, params })
    },

    async loadRefund() {
      this.loading = true
      try {
        await this.fetchMovementRefund(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    getOrderItemRoute(orderItemId) {
      return getMovementDetailRouteParams(MOVEMENT_TYPES.ORDER_ITEM, orderItemId, this.scope)
    },

  }
}
</script>

<style scoped>
.reference-link {
  color: #e67e22;
  text-decoration: underline;
  font-weight: 500;
}

.reference-link:hover {
  color: #d35400;
}
</style>
